import { FC, useCallback, useState, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { Variable, MobileForm } from '@mobileTypes'
import { DataGrid, GridColDef } from '@microservices/wiskey-react-table-component'
import { Box, Button, Paper, Typography, Chip } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { generateDataGridActionsColumn } from '@helpers'

import { VariableCreateDialog } from './VariableCreateDialog'

interface VariablesConfigTabProps {
  /** Form field name for variables array (default: 'variables'). */
  variablesFieldName?: string
}

export const VariablesConfigTab: FC<VariablesConfigTabProps> = ({
  variablesFieldName = 'variables',
}) => {
  const { watch, setValue } = useFormContext<MobileForm | Record<string, any>>()
  const variables = watch(variablesFieldName) || []

  const [dialogState, setDialogState] = useState<{
    open: boolean
    mode: 'create' | 'edit'
    editIndex?: number
    initialData?: Variable
  }>({
    open: false,
    mode: 'create',
  })

  const handleAdd = useCallback(() => {
    setDialogState({
      open: true,
      mode: 'create',
    })
  }, [])

  const handleEdit = useCallback(
    (index: number) => {
      setDialogState({
        open: true,
        mode: 'edit',
        editIndex: index,
        initialData: variables[index],
      })
    },
    [variables]
  )

  const handleDelete = useCallback(
    (index: number) => {
      const updated = variables.filter((_, idx) => idx !== index)
      setValue(variablesFieldName, updated, { shouldDirty: true })
    },
    [variables, setValue, variablesFieldName]
  )

  const handleSave = useCallback(
    (variable: Variable, _isGlobal: boolean) => {
      if (dialogState.mode === 'create') {
        // Add new variable
        setValue(variablesFieldName, [...variables, variable], { shouldDirty: true })
      } else if (dialogState.mode === 'edit' && dialogState.editIndex !== undefined) {
        // Update existing variable
        const updated = [...variables]
        updated[dialogState.editIndex] = variable
        setValue(variablesFieldName, updated, { shouldDirty: true })
      }

      setDialogState({ open: false, mode: 'create' })
    },
    [dialogState, variables, setValue, variablesFieldName]
  )

  const handleClose = useCallback(() => {
    setDialogState({ open: false, mode: 'create' })
  }, [])

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'internalId',
        headerName: 'Internal ID',
        flex: 1,
        display: 'flex',
        minWidth: 150,
        justifyContent: 'center',
        renderCell: params => (
          <Typography variant='body2' fontWeight='medium'>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'variableType',
        headerName: 'Variable Type',
        flex: 1,
        minWidth: 120,
        renderCell: params => (
          <Chip
            label={params.value}
            size='small'
            color={params.value === 'multiple' ? 'secondary' : 'default'}
          />
        ),
      },
      {
        field: 'valueType',
        headerName: 'Value Type',
        flex: 1,
        minWidth: 120,
        renderCell: params => <Chip label={params.value} size='small' variant='outlined' />,
      },
      {
        field: 'objectCode',
        headerName: 'Object Code',
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        minWidth: 150,
        renderCell: params => (
          <Typography
            variant='body2'
            color={params.value ? 'primary' : 'text.secondary'}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'defaultValue',
        headerName: 'Default Value',
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        minWidth: 200,
        renderCell: params => (
          <Typography
            variant='body2'
            sx={{
              fontFamily: 'monospace',
              fontSize: 11,
              color: params.value !== undefined && params.value !== null ? 'text.primary' : 'text.secondary',
            }}
          >
            {params.value !== undefined && params.value !== null
              ? JSON.stringify(params.value)
              : undefined}
          </Typography>
        ),
      },
      generateDataGridActionsColumn({
        onEdit: params => {
          const index = variables.findIndex(v => v.internalId === params.row.internalId)
          if (index !== -1) {
            handleEdit(index)
          }
        },
        onDelete: params => {
          const index = variables.findIndex(v => v.internalId === params.row.internalId)
          if (index !== -1) {
            handleDelete(index)
          }
        },
      }),
    ],
    [variables, handleEdit, handleDelete]
  )

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='h6'>Variables</Typography>
        <Button variant='contained' startIcon={<AddIcon />} onClick={handleAdd}>
          Add Variable
        </Button>
      </Box>

      {variables.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant='body2' color='text.secondary'>
            No variables configured. Click "Add Variable" to create one.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flex: 1, height: 400 }}>
          <DataGrid
            rows={variables}
            columns={columns}
            loading={false}
            disableRowSelectionOnClick
            hideFooter
            getRowId={row => row.internalId}
          />
        </Box>
      )}

      <VariableCreateDialog
        open={dialogState.open}
        mode={dialogState.mode}
        initialData={dialogState.initialData}
        existingVariables={variables}
        showValueTypeSelector
        showVariableTypeSelector
        showDefaultValue
        showObjectCodeSelector
        onClose={handleClose}
        onSave={handleSave}
      />
    </>
  )
}
